import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const sendCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setStep(2);
      if (data.dev_code) {
        toast.info(`Demo mode: your reset code is ${data.dev_code}`, { duration: 9000 });
        setCode(data.dev_code);
      } else {
        toast.success("If that email exists, a reset code is on its way.");
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: newPassword });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const input = "rounded-2xl bg-linen border-linen-3 h-12";

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="font-serif text-4xl text-brand block text-center mb-2">Aurea</Link>
        <p className="text-center text-ink-mute mb-8">{step === 1 ? "Let's gently restore your access." : "Enter your code and a new password."}</p>

        {step === 1 ? (
          <form onSubmit={sendCode} className="bg-white rounded-3xl border border-linen-3 p-8 space-y-5" data-testid="forgot-form">
            {error && <div className="bg-warmsoft text-warmsoft-text text-sm rounded-2xl px-4 py-3" data-testid="forgot-error">{error}</div>}
            <div className="space-y-2">
              <Label className="text-ink-2">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} data-testid="forgot-email" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand hover:bg-brand-hover text-linen h-12" data-testid="forgot-send">
              {loading ? "Sending…" : "Send reset code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={reset} className="bg-white rounded-3xl border border-linen-3 p-8 space-y-5" data-testid="reset-form">
            {error && <div className="bg-warmsoft text-warmsoft-text text-sm rounded-2xl px-4 py-3" data-testid="reset-error">{error}</div>}
            <div className="space-y-2">
              <Label className="text-ink-2">Reset code</Label>
              <Input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className={input} data-testid="reset-code" />
            </div>
            <div className="space-y-2">
              <Label className="text-ink-2">New password</Label>
              <Input type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={input} data-testid="reset-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand hover:bg-brand-hover text-linen h-12" data-testid="reset-submit">
              {loading ? "Resetting…" : "Reset password"}
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-ink-2 mt-6">
          Remembered it? <Link to="/login" className="text-accent font-medium" data-testid="back-to-login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
